package com.trevorwiebe.timesheet.admin.employeeHours.domain.usecases

import com.trevorwiebe.timesheet.core.domain.Util.between
import com.trevorwiebe.timesheet.core.domain.model.Rate
import com.trevorwiebe.timesheet.punch.domain.usecases.roundToNearestQuarter
import com.trevorwiebe.timesheet.punch.presentation.uiUtils.UiPunch
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.daysUntil
import kotlin.time.Duration

/**
 * Adds up a pay period's hours by rate like [com.trevorwiebe.timesheet.punch.domain.usecases.AddUpHours],
 * but splits out weekly overtime for the payroll export: within each 7-day week of the period
 * (days 0–6, 7–13, ... from [periodStart]), the first 40 worked hours keep their rate and every
 * hour past 40 is attributed to the rate being worked at that moment — a punch straddling the
 * 40-hour mark splits. Overtime buckets are labeled "<description> OT Hours".
 *
 * PTO/Holiday punches ([ptoHolidayRateIds]) total normally under their own rate and never count
 * toward the 40-hour threshold.
 */
class AddUpHoursWithOvertime {

    operator fun invoke(
        uiPunchList: List<UiPunch>,
        rateList: List<Rate>,
        ptoHolidayRateIds: List<String>,
        periodStart: LocalDateTime,
    ): List<Pair<String, Double>> {
        if (uiPunchList.isEmpty()) return emptyList()

        // Minutes keyed by (rate id, isOvertime). Punches with an unknown rate are dropped,
        // matching AddUpHours.
        val minutes = mutableMapOf<Pair<String, Boolean>, Long>()
        fun add(rateId: String, overtime: Boolean, mins: Long) {
            val key = rateId to overtime
            minutes[key] = (minutes[key] ?: 0L) + mins
        }

        val (ptoHoliday, worked) = uiPunchList
            .filter { it.punchOut != null }
            .partition { it.punchIn.rateId in ptoHolidayRateIds }

        ptoHoliday.forEach { add(it.punchIn.rateId, false, it.durationMinutes()) }

        // Walk each week's worked punches chronologically; minutes past 40h go to that punch's
        // rate as overtime.
        worked
            .groupBy { periodStart.date.daysUntil(it.punchIn.dateTime.date) / 7 }
            .values
            .forEach { weekPunches ->
                var cumulative = 0L
                weekPunches.sortedBy { it.punchIn.dateTime }.forEach { punch ->
                    val duration = punch.durationMinutes()
                    val regular = minOf(duration, maxOf(0L, WEEKLY_OT_THRESHOLD_MINUTES - cumulative))
                    val overtime = duration - regular
                    if (regular != 0L) add(punch.punchIn.rateId, false, regular)
                    if (overtime != 0L) add(punch.punchIn.rateId, true, overtime)
                    cumulative += duration
                }
            }

        return minutes.mapNotNull { (key, mins) ->
            val (rateId, overtime) = key
            val rate = rateList.find { it.id == rateId } ?: return@mapNotNull null
            val hours = roundToNearestQuarter(mins / 60.0)
            if (hours == 0.0) return@mapNotNull null
            val label = if (overtime) "${rate.description} OT Hours" else "${rate.description} Hours"
            label to hours
        }.sortedBy { it.first }
    }

    private fun UiPunch.durationMinutes(): Long =
        Duration.between(punchIn.dateTime, punchOut!!.dateTime).inWholeMinutes

    companion object {
        private const val WEEKLY_OT_THRESHOLD_MINUTES = 40L * 60L
    }
}
